import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { canOpenQuotePage, isQuoteOwner } from '@/lib/quotePermissions';
import {
  FileText, Plus, Users, Package, Settings, Building2, LogOut, Menu, X, MessageSquareWarning, BarChart3, Boxes, UserCog, Wrench,
} from 'lucide-react';

/**
 * QuoteSidebar
 * ------------
 * Standalone navigation for the Quote Builder module. Intentionally
 * decoupled from the legacy Paydaai `Sidebar`: shows only the six
 * items the user asked for and nothing from the old panels
 * (payments / appointments / SMS / branches / announcements etc.).
 *
 * Reuses the same visual language (colors, spacing, shadcn button) so
 * the switch feels native, but the code path is independent.
 */
export const QuoteSidebar = ({ onReportIssue }) => {
  const { t } = useTranslation('quotes');
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const links = [
    { to: '/quotes', icon: FileText, label: t('nav.quotes') },
    { to: '/quotes/new', icon: Plus, label: t('list.newQuote') },
    { to: '/quote-customers', icon: Users, label: t('nav.customers') },
    { to: '/quote-catalog', icon: Package, label: t('nav.catalog') },
    { to: '/quote-stock', icon: Boxes, label: t('nav.stock') },
    { to: '/quote-service', icon: Wrench, label: t('nav.service') },
    { to: '/quote-insights', icon: BarChart3, label: t('nav.insights') },
    { to: '/company-settings', icon: Building2, label: t('nav.companySettings') },
    { to: '/quote-team', icon: UserCog, label: t('nav.team') },
    { to: '/quote-settings', icon: Settings, label: t('nav.settings') },
  ].filter((link) => canOpenQuotePage(user, link.to));

  // Keep the compact mobile bottom bar. Company settings and stock remain
  // available from the mobile drawer so the bottom bar does not get crowded.
  // Owners keep the original bar; staff see stock there since their menu is shorter.
  const hiddenInBar = ['/company-settings', '/quote-team', ...(isQuoteOwner(user) ? ['/quote-stock', '/quote-service'] : [])];
  const bottomLinks = links.filter((link) => !hiddenInBar.includes(link.to));

  const isActive = (to) => {
    if (to === '/quotes/new') return location.pathname === '/quotes/new';
    if (to === '/quotes') {
      // /quotes list — active on exact match or /quotes/:id (but NOT /quotes/new)
      return (
        location.pathname === '/quotes'
        || (location.pathname.startsWith('/quotes/') && location.pathname !== '/quotes/new')
      );
    }
    return location.pathname === to;
  };

  return (
    <>
      {/* Mobile top bar */}
      <div
        className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-white border-b border-[#E5E5E5] z-40 flex items-center px-3 gap-2"
        data-testid="quote-mobile-topbar"
      >
        <button
          onClick={() => setMobileOpen(true)}
          className="p-1.5 -ml-1 shrink-0"
          data-testid="quote-open-sidebar"
        >
          <Menu className="w-6 h-6 text-[#1A1A1A]" />
        </button>
        <img
          src="/logo-paydaai.png"
          alt="Paydaai"
          className="h-10 w-auto max-w-[150px] object-contain shrink-0 drop-shadow-sm"
          data-testid="quote-mobile-topbar-logo"
        />
        <div className="flex-1 min-w-0 flex justify-end">
          <span
            className="text-xs font-medium text-[#1A1A2E] truncate max-w-[50%]"
            title={t('moduleTitle')}
          >
            {t('moduleTitle')}
          </span>
        </div>
      </div>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/40 z-50"
          onClick={() => setMobileOpen(false)}
          data-testid="quote-sidebar-overlay"
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed lg:sticky top-0 left-0 h-screen bg-white border-r border-[#E5E5E5] flex flex-col z-50 transition-transform duration-300 w-64 ${
          mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        }`}
        data-testid="quote-sidebar"
      >
        <div className="p-4 border-b border-[#E5E5E5]">
          <div className="flex flex-col items-center gap-1.5 relative">
            <button
              onClick={() => setMobileOpen(false)}
              className="lg:hidden absolute top-0 right-0 p-1"
              data-testid="quote-close-sidebar"
            >
              <X className="w-5 h-5 text-[#595959]" />
            </button>
            <img
              src="/logo-paydaai.png"
              alt="Paydaai"
              className="h-12 w-auto object-contain drop-shadow-md"
            />
          </div>
          <div className="mt-3 px-1 text-center">
            <p className="text-sm font-medium text-[#1A1A2E] truncate" data-testid="quote-sidebar-module-name">
              {t('moduleTitle')}
            </p>
            <p className="text-[11px] text-[#999]">PaydaAI</p>
          </div>
        </div>

        <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto">
          {links.map((link) => {
            const Icon = link.icon;
            const active = isActive(link.to);
            return (
              <Link
                key={link.to}
                to={link.to}
                onClick={() => setMobileOpen(false)}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-all ${
                  active
                    ? 'bg-[#2563EB] text-white font-medium shadow-sm'
                    : 'text-[#475569] hover:bg-[#EFF6FF] hover:text-[#1E4ED8]'
                }`}
                data-testid={`quote-nav-${link.to.replace(/\//g, '-').slice(1)}`}
              >
                <Icon className="w-[18px] h-[18px]" strokeWidth={active ? 2 : 1.5} />
                <span>{link.label}</span>
              </Link>
            );
          })}
          <button
            type="button"
            onClick={() => {
              setMobileOpen(false);
              onReportIssue?.();
            }}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-[#475569] hover:bg-[#FFF7ED] hover:text-[#C2410C] transition-all"
            data-testid="quote-nav-report-issue"
          >
            <MessageSquareWarning className="w-[18px] h-[18px]" strokeWidth={1.5} />
            <span>{t('nav.reportIssue', { defaultValue: 'Sorun / Öneri Bildir' })}</span>
          </button>
        </nav>

        <div className="p-3 border-t border-[#E5E5E5]">
          <div className="mb-3 px-3">
            <p className="text-sm font-medium text-[#1A1A2E] truncate" data-testid="quote-user-name">
              {user?.name}
            </p>
            <p className="text-[11px] text-[#999] truncate" data-testid="quote-user-email">
              {user?.email}
            </p>
          </div>
          <Button
            onClick={handleLogout}
            variant="ghost"
            className="w-full justify-start gap-3 text-[#595959] hover:text-red-600 hover:bg-red-50 h-9"
            data-testid="quote-logout-button"
          >
            <LogOut className="w-4 h-4" strokeWidth={1.5} /> {t('nav.logout')}
          </Button>
        </div>
      </aside>

      {/* Mobile bottom nav */}
      <nav
        className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-[#E5E5E5] z-40 pb-[env(safe-area-inset-bottom)]"
        data-testid="quote-bottom-nav"
      >
        <div className="flex items-stretch h-14">
          {bottomLinks.map((link) => {
            const Icon = link.icon;
            const active = isActive(link.to);
            return (
              <Link
                key={link.to}
                to={link.to}
                className={`flex-1 flex flex-col items-center justify-center gap-0.5 transition-colors ${
                  active ? 'text-[#2563EB]' : 'text-[#999]'
                }`}
                data-testid={`quote-bottom-${link.to.replace(/\//g, '-').slice(1)}`}
              >
                <Icon className="w-5 h-5" strokeWidth={active ? 2.5 : 1.5} />
                <span
                  className={`text-[10px] leading-tight truncate max-w-[64px] ${active ? 'font-semibold' : ''}`}
                >
                  {link.label}
                </span>
              </Link>
            );
          })}
        </div>
      </nav>
    </>
  );
};

export default QuoteSidebar;
